import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FC } from 'react';
import { DropzoneProps, useDropzone } from 'react-dropzone';
import { useIntl } from 'react-intl';
import messages from './messages';

interface FileDropzoneProps extends Pick<DropzoneProps, 'onDrop'> {
  allowedFileTypes?: string;
  selectedFile?: File | null;
  allowContinue?: boolean;
  onClickContinue?: () => void;
}

export const FileDropzone: FC<FileDropzoneProps> = ({ onDrop, allowedFileTypes, selectedFile }) => {
  const { formatMessage } = useIntl();
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const dropBoxStyles = {
    cursor: 'pointer',
    backgroundColor: (theme) =>
      isDragActive ? theme.palette.grey['400'] : theme.palette.background.paper,
    borderRadius: '8px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '225px',
  };

  if (selectedFile) {
    return (
      <Box {...getRootProps()} sx={dropBoxStyles}>
        <input {...getInputProps()} accept={allowedFileTypes} />
        <InsertDriveFileIcon
          color="action"
          sx={{
            fontSize: 36,
            color: (theme) => theme.palette.grey[900],
          }}
        />
        <Typography
          variant="subtitle1"
          sx={{ marginTop: (theme) => theme.spacing(1), marginBottom: (theme) => theme.spacing(2) }}
        >
          {selectedFile.name}
        </Typography>
        <Typography variant="subtitle2" sx={{ opacity: 0.5 }}>
          {formatFileSize(selectedFile.size)}
        </Typography>
      </Box>
    );
  }

  return (
    <Box {...getRootProps()} sx={dropBoxStyles}>
      <input {...getInputProps()} accept={allowedFileTypes} />
      <CloudUploadIcon
        color="action"
        sx={{
          fontSize: 54,
          marginBottom: (theme) => theme.spacing(1),
          color: (theme) => theme.palette.grey[900],
          opacity: 0.5,
        }}
      />
      <Typography variant="subtitle2" sx={{ opacity: 0.5 }}>
        {formatMessage(messages.instrucitonDragAndDrop)}
      </Typography>
      <Typography variant="subtitle2" sx={{ opacity: 0.5 }}>
        {formatMessage(messages.instructionOr)}
      </Typography>
      <Typography
        variant="subtitle1"
        sx={{
          textDecoration: 'underline',
          marginTop: (theme) => theme.spacing(1),
        }}
      >
        {formatMessage(messages.instructionBrowse)}
      </Typography>
    </Box>
  );
};

const formatFileSize = (fileSize: number): string => {
  if (fileSize < 1000) return '<1 KB';
  if (fileSize < 1000000) return `${(fileSize / 1000).toFixed(0)} KB`;
  if (fileSize < 1000000000) return `${(fileSize / 1000000).toFixed(0)} MB`;
  return `${fileSize / 1000000000} GB`;
};
